<template>
    <div class="baggage-list">
        <div v-if="hasItems" class="baggage-list-inner">
            <RadioField
                v-model="selected"
                :name="fieldName"
                :input="{ items: options, density: 'comfortable' }" />
        </div>
        <div v-else class="baggage-empty">{{ noOptionsLabel }}</div>
    </div>
</template>

<script lang="ts">
    import { computed, watch, ref, defineComponent, PropType, inject } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { isEmpty } from 'lodash';
    import { getBaggageLabel, getBaggagePrice } from '@shared/helpers/baggage';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { Baggage, BaggageType, LegType, NO_SELECTION } from '@shared/interfaces/baggage';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { getFieldName } from '../../BaggageSelectionForm.helpers';
    import {
        NoSelectionType,
        SelectionMetaData,
    } from '../../BaggageSelectionForm.interfaces';
    import RadioField from '@ui/components/FormV2/Fields/Radio/RadioField.vue';

    export default defineComponent({
        name: 'List',
        components: { RadioField },
        methods: { t },
        props: {
            items: { type: Array as PropType<Baggage[]>, default: () => [] },
            visibleCount: { type: Number, default: 3 },
            isOneWay: { type: Boolean, default: false },
            hideEmptyAlternatives: {
                type: Boolean,
                default: false,
            },
            type: {
                type: String as PropType<BaggageType>,
                required: true,
            },
            leg: {
                type: String as PropType<LegType>,
                required: true,
            },
            onSelection: {
                required: true,
                type: Function as PropType<
                    (
                        selection: Baggage | NoSelectionType,
                        ids: string[],
                        meta: SelectionMetaData
                    ) => void
                >,
            },
            noBaggageSelectionCabinBagLabel: { type: String, required: true },
            noBaggageSelectionCheckedBagLabel: { type: String, required: true },
            noBaggageSelectionLabel: { type: String, required: true },
            noBaggageOptionsLabel: { type: String },
        },
        setup(props) {
            const selected = ref<string | null>();
            const noSelectionChecked = ref<boolean>(false);

            const paxIndex = inject('paxIndex') as number;

            const hasItems = computed((): boolean => props.items && !isEmpty(props.items));

            const hasSelectedItems = computed((): boolean =>
                props.items.some((bag: Baggage) => bag.IsSelected)
            );

            const fieldName = computed(() => getFieldName(props.type, props.leg, paxIndex));

            const noSelectionLabel = computed(() => getNoSelectionLabel());
            const noOptionsLabel = computed(
                () => props.noBaggageOptionsLabel || noSelectionLabel.value
            );

            const getNoSelectionLabel = () => {
                switch (props.type) {
                    case BaggageType.CabinBag:
                        return props.noBaggageSelectionCabinBagLabel;
                    case BaggageType.CheckedBag:
                        return props.noBaggageSelectionCheckedBagLabel;
                    default:
                        return props.noBaggageSelectionLabel;
                }
            };

            const options = computed((): SelectItem[] => {
                return [
                    {
                        value: NO_SELECTION,
                        label: noSelectionLabel.value,
                    },
                    ...props.items.map((bag: Baggage) => {
                        const price = getBaggagePrice(bag.Price, props.leg, props.isOneWay);

                        return {
                            value: bag.Id,
                            label: getBaggageLabel(bag, false, false),
                            subLabel: getMoneyObjectAsString(price.Price),
                            subLabelExtra: price.Split
                                ? `${t({ key: 'shared.per_route' })}`
                                : undefined,
                        };
                    }),
                ];
            });

            watch(
                hasSelectedItems,
                (newValue) => {
                    if (newValue) {
                        noSelectionChecked.value = false;

                        const bag = props.items.find((x: Baggage) => x.IsSelected);

                        selected.value = bag ? bag.Id : null;
                    }
                },
                { immediate: true }
            );

            watch(selected, (newValue, prevValue) => {
                if (newValue && newValue !== prevValue) {
                    const ids = props.items.map((bag: Baggage) => bag.Id);

                    props.onSelection(getBaggage(newValue), ids, {
                        type: props.type,
                        leg: props.leg,
                    });
                }
            });

            const getBaggage = (id: string | null): Baggage | NoSelectionType => {
                return props.items.find((bag: Baggage) => bag.Id === id) ?? NO_SELECTION;
            };

            return {
                selected,
                options,
                hasItems,
                fieldName,
                noOptionsLabel,
                noSelectionLabel,
                noSelectionChecked,
            };
        },
    });
</script>
